import React, { useState, useEffect } from "react";
import {
  FileText, Clock, Calendar, DollarSign,
  Search, X, Shield, MessageCircle,
  CheckCircle, AlertCircle, Filter,
  List, Grid, Star, User, Package,
  MoreHorizontal, ChevronDown, ThumbsUp,
  ThumbsDown, Timer, Briefcase, MapPin,
  Mail, Phone, Send, Heart, Download, Eye
} from "lucide-react";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/Button";
import { Badge } from "../components/ui/badge";
import SharedHeader5 from "../Headers/SharedHeader5";
import SharedFooter3 from "../Footer/SharedFooter3";
import { useAppDispatch, useAppSelector } from "../redux/store";
import { fetchUserOffers, updateOffer } from "../redux/Slices/offerSlice";
import { RequestQuoteModal } from "../components/ui";
import { OfferStatusEnum, OfferTimelineTypeEnum } from "../modules/shared/model/offer.interfaces";
import { RoleEnum } from "../modules";
import { GlobalService } from "../modules/shared/services/global.service";
import { useNavigate } from "react-router-dom";
import { createConversation, createChat } from "../redux/Actions/AuthAction";
import { fetchParticipantChats, updateChat } from "../redux/Actions/ChatActions";
import { ConversationTypeEnum } from "../modules/shared/model/conversationType.enum";
import { ContextEnum } from "../modules/shared/model/context.enum";
import { useWebSocket } from "../hooks/useWebSocket";
import MyProfile from "../modules/professional/components/MyProfile";
import { motion, AnimatePresence } from "framer-motion";
import ContractMilestoneModal from "../components/ui/ContractMilestoneModal";
import { getDynamicCurrencySymbol } from "../lib/currencyUtils";
import ClientOfferDetailsModal from "../components/ui/ClientOfferDetailsModal";
import { calculateEmergencyCost } from "../lib/emergencyCostUtils";
import { useTranslation } from "react-i18next";


const MyOffersPage = () => {
  const { t } = useTranslation();
  // Redux
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state: any) => state.auth);
  const { offers, loading, error, totalCount, currentPage, totalPages, hasNextPage } = useAppSelector((state: any) => state.offers);
  const { settings: categoryFormSettings } = useAppSelector((state: any) => state.categoryFormSettings);
  const { sendMessage: sendWebSocketMessage, isConnected } = useWebSocket({
    onConnect: () => {
      // WebSocket connected
    },
    onDisconnect: () => {
      // WebSocket disconnected
    },
    onError: (error) => {
      console.error("WebSocket error:", error);
    }
  });
  const navigate = useNavigate();

  // State management
  const [selectedTab, setSelectedTab] = useState(() => {
    // Get the saved tab from localStorage, default to "received"
    return localStorage.getItem("myOffersSelectedTab") || "received";
  });
  const [sentTabCount, setSentTabCount] = useState(0);
  const [receivedTabCount, setReceivedTabCount] = useState(0);
  const [viewMode, setViewMode] = useState("list");
  const [filterOpen, setFilterOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    offerType: "all",
    status: "all",
    timeframe: "all",
  });
  const [localCurrentPage, setLocalCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [isFiltering, setIsFiltering] = useState(false);
  const [countsLoading, setCountsLoading] = useState(false);
  const [countsInitialized, setCountsInitialized] = useState(false);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);

  // Popover state
  const [popoverOpen, setPopoverOpen] = useState<string | null>(null);
  const [editingOffer, setEditingOffer] = useState<any>(null);
  const [showRequestQuoteModal, setShowRequestQuoteModal] = useState(false);

  // Profile dialog state
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [selectedProfessional, setSelectedProfessional] = useState<any>(null);
  // Contract modal state
  const [showContractModal, setShowContractModal] = useState(false);
  const [contractOffer, setContractOffer] = useState<any>({});

  // Single function to fetch all counts with proper loading state management
  const fetchAllCounts = async (forceRefresh = false) => {
    if (!user?.id || (countsLoading && !forceRefresh)) return;
    
    setCountsLoading(true);
    try {
      // Fetch both counts in parallel but with proper error handling
      const [receivedResponse, sentResponse] = await Promise.allSettled([
        // Received offers count
        dispatch(fetchUserOffers({
          page: 1,
          limit: 1, // Only need the count, so limit to 1
          jobUserId: user.id,
          isSent: false,
          statusIds: [OfferStatusEnum.Approved, OfferStatusEnum.Accepted, OfferStatusEnum.Rejected, OfferStatusEnum.Cancelled, OfferStatusEnum.Archived, OfferStatusEnum.Admin_Suspended, OfferStatusEnum.Admin_Rejected, OfferStatusEnum.Admin_Deleted].toString(),
          sort: [{ orderBy: "createdAt", order: "DESC" }]
        })),
        // Sent offers count
        dispatch(fetchUserOffers({
          page: 1,
          limit: 1, // Only need the count, so limit to 1
          jobUserId: user.id,
          isSent: true,
          statusIds: [OfferStatusEnum.Approved, OfferStatusEnum.Accepted, OfferStatusEnum.Rejected, OfferStatusEnum.Cancelled, OfferStatusEnum.Archived, OfferStatusEnum.Admin_Suspended, OfferStatusEnum.Admin_Rejected, OfferStatusEnum.Admin_Deleted].toString(),
          sort: [{ orderBy: "createdAt", order: "DESC" }]
        }))
      ]);

      // Update counts based on successful responses
      if (receivedResponse.status === "fulfilled" && receivedResponse.value?.meta?.requestStatus === "fulfilled") {
        const payload = receivedResponse.value.payload as any;
        const totalCount = payload?.totalCount || 0;
        setReceivedTabCount(Number(totalCount) || 0);
      }

      if (sentResponse.status === "fulfilled" && sentResponse.value?.meta?.requestStatus === "fulfilled") {
        const payload = sentResponse.value.payload as any;
        const totalCount = payload?.totalCount || 0;
        setSentTabCount(Number(totalCount) || 0);
      }

      setCountsInitialized(true);
    } catch (error) {
      console.error("Error fetching counts:", error);
    } finally {
      setCountsLoading(false);
    }
  };

  // Load counts on component mount
  useEffect(() => {
    if (user?.id && !countsInitialized) {
      fetchAllCounts();
    }
  }, [user?.id, countsInitialized]);

  // Fetch offers based on selected tab, search, filters, and pagination
  useEffect(() => {
    if (user?.id) {
      const fetchOffers = async () => {
        try {
          console.log("Fetching offers - Page:", localCurrentPage, "Tab:", selectedTab, "Search:", searchTerm, "Filters:", filters);
          setIsFiltering(true);
          // Fetch offers based on selected tab with current filters
          await dispatch(fetchUserOffers({
            page: localCurrentPage,
            limit: pageSize,
            jobUserId: user.id,
            isSent: selectedTab === "sent",
            search: searchTerm || undefined,
            status: filters.status !== "all" ? (filters.status === "approved" ? 2 : filters.status === "accepted" ? 3 : filters.status === "rejected" ? 4 : filters.status === "expired" ? 5 : filters.status === "cancelled" ? 6 : filters.status === "completed" ? 7 : filters.status === "archived" ? 8 : filters.status === "deleted" ? 9 : filters.status === "permanently_deleted" ? 10 : undefined) : undefined,
            statusIds: [OfferStatusEnum.Approved, OfferStatusEnum.Accepted, OfferStatusEnum.Rejected, OfferStatusEnum.Cancelled, OfferStatusEnum.Archived, OfferStatusEnum.Admin_Suspended, OfferStatusEnum.Admin_Rejected, OfferStatusEnum.Admin_Deleted].toString(),
            sort: [{ orderBy: "createdAt", order: "DESC" }]
          })).unwrap();
          setIsFiltering(false); // Reset filtering state when API call completes
        } catch (error) {
          console.error("Error fetching offers:", error);
          setIsFiltering(false); // Reset filtering state on error
        }
      };

      fetchOffers();
    }
  }, [dispatch, user?.id, selectedTab, filters, localCurrentPage, pageSize]);

  // Remove the old count fetching logic that was causing conflicts
  // Counts are now managed by fetchAllCounts function


  // Debounced search effect - reset to page 1 when search term changes
  useEffect(() => {
    const timer = setTimeout(() => {
      if (user?.id && searchTerm) {
        setLocalCurrentPage(1); // Reset to first page when searching
      }
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchTerm, user?.id]);

  // Reset pagination when filters change
  useEffect(() => {
    setLocalCurrentPage(1);
  }, [filters]);

  // Handle filter changes
  const handleFilterChange = (filterType: string, value: string) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
    setLocalCurrentPage(1); // Reset to first page
    setIsFiltering(true);
  };

  // Handle tab change
  const handleTabChange = (tab: string) => {
    setSelectedTab(tab);
    // Save the selected tab to localStorage
    localStorage.setItem("myOffersSelectedTab", tab);
    setLocalCurrentPage(1); // Reset to first page
    setSearchTerm(""); // Clear search when changing tabs
  };

  // Handle popover actions
  const handleEditOffer = (offer: any) => {
    // Format the offer data to match the expected

    setEditingOffer(offer);
    setShowRequestQuoteModal(true);
    setPopoverOpen(null); // Close popover
  };

  const handleUpdateStatusOffer = async (offer: any, status: number) => {
    let confirmationMessage = "";

    if (status === OfferStatusEnum.Cancelled) {
      confirmationMessage = t("myOffersPage.confirmSuspend");
    } else if (status === OfferStatusEnum.Approved) {
      confirmationMessage = t("myOffersPage.confirmApprove");
    } else if (status === OfferStatusEnum.Admin_Suspended) {
      confirmationMessage = t("myOffersPage.confirmSuspend");
    } else if (status === OfferStatusEnum.Admin_Rejected) {
      confirmationMessage = t("myOffersPage.confirmReject");
    }

    if (confirmationMessage) {
      const isConfirmed = window.confirm(confirmationMessage);
      if (!isConfirmed) return;
    }

    try {
      const originalOffer = offer.origionalOffer;
      // Update offer status to Suspended (assuming status 6 is for suspended/cancelled)
      await dispatch(updateOffer({
        id: offer.id,
        data: {
          status: { id: status }
        } // Cancelled/Suspended status
      })).unwrap();

      // Refresh offers and counts
      if (user?.id) {
        dispatch(fetchUserOffers({
          page: localCurrentPage,
          limit: pageSize,
          jobUserId: user.id,
          isSent: selectedTab === "sent",
          search: searchTerm || undefined,
          status: filters.status !== "all" ? (filters.status === "pending" ? 2 : filters.status === "accepted" ? 3 : filters.status === "declined" ? 4 : filters.status === "expired" ? 5 : filters.status === "cancelled" ? 6 : filters.status === "completed" ? 7 : filters.status === "archived" ? 8 : filters.status === "deleted" ? 9 : filters.status === "permanently_deleted" ? 10 : undefined) : undefined,
          statusIds: [OfferStatusEnum.Approved, OfferStatusEnum.Accepted, OfferStatusEnum.Rejected, OfferStatusEnum.Cancelled].toString(),
          sort: [{ orderBy: "createdAt", order: "DESC" }]
        }));
        // Refresh counts after offer update
        fetchAllCounts(true);
      }

      setPopoverOpen(null); // Close popover
      let statusText = "updated";
      if (status === OfferStatusEnum.Cancelled || status === OfferStatusEnum.Admin_Suspended) {
        statusText = "suspended";
      } else if (status === OfferStatusEnum.Approved) {
        statusText = "approved";
      } else if (status === OfferStatusEnum.Admin_Rejected) {
        statusText = "rejected";
      }
      GlobalService.showToast("success", t("common.success"), t("myOffersPage.offerUpdatedSuccess", { status: statusText }));
    } catch (error) {
      let statusText = "update";
      if (status === OfferStatusEnum.Cancelled || status === OfferStatusEnum.Admin_Suspended) {
        statusText = "suspend";
      } else if (status === OfferStatusEnum.Approved) {
        statusText = "approve";
      } else if (status === OfferStatusEnum.Admin_Rejected) {
        statusText = "reject";
      }
      GlobalService.showToast("error", t("common.error"), t("myOffersPage.offerUpdateError", { action: statusText }));
    }
  };

  // Handle offer acceptance
  const handleAcceptOffer = async (offer: any) => {
    try {
      console.log("🔌 handleAcceptOffer called with offer:", offer);
      const originalOffer = offer.origionalOffer;
      console.log("🔌 originalOffer:", originalOffer);
      // Update offer status to accepted (status ID: 3)
      await dispatch(updateOffer({
        id: offer.id,
        data: {
          status: { id: OfferStatusEnum.Accepted }
        }
      })).unwrap();

      // Send conversation message
      await createChatAndSendMessage(
        offer,
        ConversationTypeEnum.Quote_Accepted,
        "Quote has been accepted"
      );

        // Refresh offers and counts
        if (user?.id) {
          dispatch(fetchUserOffers({
          page: localCurrentPage,
          limit: pageSize,
          jobUserId: user.id,
          isSent: selectedTab === "sent",
          search: searchTerm || undefined,
          status: filters.status !== "all" ? (filters.status === "approved" ? 2 : filters.status === "accepted" ? 3 : filters.status === "rejected" ? 4 : filters.status === "expired" ? 5 : filters.status === "cancelled" ? 6 : filters.status === "completed" ? 7 : filters.status === "archived" ? 8 : filters.status === "deleted" ? 9 : undefined) : undefined,
          statusIds: [OfferStatusEnum.Approved, OfferStatusEnum.Accepted, OfferStatusEnum.Rejected, OfferStatusEnum.Cancelled, OfferStatusEnum.Archived, OfferStatusEnum.Admin_Suspended, OfferStatusEnum.Admin_Rejected, OfferStatusEnum.Admin_Deleted].toString(),
          sort: [{ orderBy: "createdAt", order: "DESC" }]
        }));
        // Refresh counts after offer update
        fetchAllCounts(true);
      }

      GlobalService.showToast("success", t("common.success"), t("clientOfferDetailsModal.offerAcceptedSuccess"));
    } catch (error) {
      console.error("Error accepting offer:", error);
      GlobalService.showToast("error", t("common.error"), t("clientOfferDetailsModal.offerAcceptedError"));
    }
  };

  // Handle offer decline
  const handleDeclineOffer = async (offer: any) => {
    try {
      const originalOffer = offer.origionalOffer;
      // Update offer status to rejected (status ID: 4)
      await dispatch(updateOffer({
        id: offer.id,
        data: {
          status: { id: OfferStatusEnum.Rejected }
        }
      })).unwrap();

      // Send conversation message
      await createChatAndSendMessage(
        offer,
        ConversationTypeEnum.Quote_Rejected,
        "Quote has been rejected"
      );

        // Refresh offers and counts
        if (user?.id) {
          dispatch(fetchUserOffers({
          page: localCurrentPage,
          limit: pageSize,
          jobUserId: user.id,
          isSent: selectedTab === "sent",
          search: searchTerm || undefined,
          status: filters.status !== "all" ? (filters.status === "approved" ? 2 : filters.status === "accepted" ? 3 : filters.status === "rejected" ? 4 : filters.status === "expired" ? 5 : filters.status === "cancelled" ? 6 : filters.status === "completed" ? 7 : filters.status === "archived" ? 8 : filters.status === "deleted" ? 9 : undefined) : undefined,
          statusIds: [OfferStatusEnum.Approved, OfferStatusEnum.Accepted, OfferStatusEnum.Rejected, OfferStatusEnum.Cancelled, OfferStatusEnum.Archived, OfferStatusEnum.Admin_Suspended, OfferStatusEnum.Admin_Rejected, OfferStatusEnum.Admin_Deleted].toString(),
          sort: [{ orderBy: "createdAt", order: "DESC" }]
        }));
        // Refresh counts after offer update
        fetchAllCounts(true);
      }

      GlobalService.showToast("success", t("common.success"), t("clientOfferDetailsModal.offerDeclinedSuccess"));
    } catch (error) {
      GlobalService.showToast("error", t("common.error"), t("clientOfferDetailsModal.offerDeclinedError"));
    }
  };

  const handleDeleteOffer = async (offer: any) => {
    const isConfirmed = window.confirm(
      t("myOffersPage.confirmDelete")
    );

    if (!isConfirmed) return;

    try {
      const originalOffer = offer.origionalOffer;
      // Update offer status to Deleted (status 9)
      await dispatch(updateOffer({
        id: offer.id,
        data: {
          status: { id: 9 },
          timelineType: { id: Number(originalOffer.timelineType.id) },
          files: originalOffer.files || [],
          user: { id: originalOffer.user.id },
          job: { id: originalOffer.job.id },
          amount: originalOffer.amount || 0,
          title: originalOffer.title || "",
          description: originalOffer.description || "",
          isWarranty: originalOffer.isWarranty || false,
          isPartsIncluded: originalOffer.isPartsIncluded || false,
          timeline: originalOffer.timeline || 1,
        }

      })).unwrap();

        // Refresh offers and counts
        if (user?.id) {
          dispatch(fetchUserOffers({
          page: localCurrentPage,
          limit: pageSize,
          jobUserId: user.id,
          isSent: selectedTab === "sent",
          search: searchTerm || undefined,
          status: filters.status !== "all" ? (filters.status === "approved" ? 2 : filters.status === "accepted" ? 3 : filters.status === "rejected" ? 4 : filters.status === "expired" ? 5 : filters.status === "cancelled" ? 6 : filters.status === "completed" ? 7 : filters.status === "archived" ? 8 : filters.status === "deleted" ? 9 : undefined) : undefined,
          statusIds: [OfferStatusEnum.Approved, OfferStatusEnum.Accepted, OfferStatusEnum.Rejected, OfferStatusEnum.Cancelled, OfferStatusEnum.Archived, OfferStatusEnum.Admin_Suspended, OfferStatusEnum.Admin_Rejected, OfferStatusEnum.Admin_Deleted].toString(),
          sort: [{ orderBy: "createdAt", order: "DESC" }]
        }));
        // Refresh counts after offer update
        fetchAllCounts(true);
      }

      setPopoverOpen(null); // Close popover
      GlobalService.showToast("success", t("common.success"), t("myOffersPage.offerDeletedSuccess"));
    } catch (error) {
      GlobalService.showToast("error", t("common.error"), t("myOffersPage.offerDeleteError"));
    }
  };

  // Handle offer permanent deletion with confirmation
  const handlePermanentlyDeleteOffer = async (offer: any) => {
    const isConfirmed = window.confirm(
      t("myOffersPage.confirmPermanentDelete")
    );

    if (!isConfirmed) return;

    try {
      const originalOffer = offer.origionalOffer;
      // Update offer status to permanently deleted (status ID: 10)
      await dispatch(updateOffer({
        id: offer.id,
        data: {
          status: { id: 10 },
          timelineType: { id: Number(originalOffer.timelineType.id) },
          files: originalOffer.files || [],
          user: { id: originalOffer.user.id },
          job: { id: originalOffer.job.id },
          amount: originalOffer.amount || 0,
          title: originalOffer.title || "",
          description: originalOffer.description || "",
          isWarranty: originalOffer.isWarranty || false,
          isPartsIncluded: originalOffer.isPartsIncluded || false,
          timeline: originalOffer.timeline || 1,
        }
      })).unwrap();

        // Refresh offers and counts
        if (user?.id) {
          dispatch(fetchUserOffers({
          page: localCurrentPage,
          limit: pageSize,
          jobUserId: user.id,
          isSent: selectedTab === "sent",
          search: searchTerm || undefined,
          status: filters.status !== "all" ? (filters.status === "approved" ? 2 : filters.status === "accepted" ? 3 : filters.status === "rejected" ? 4 : filters.status === "expired" ? 5 : filters.status === "cancelled" ? 6 : filters.status === "completed" ? 7 : filters.status === "archived" ? 8 : filters.status === "deleted" ? 9 : undefined) : undefined,
          statusIds: [OfferStatusEnum.Approved, OfferStatusEnum.Accepted, OfferStatusEnum.Rejected, OfferStatusEnum.Cancelled, OfferStatusEnum.Archived, OfferStatusEnum.Admin_Suspended, OfferStatusEnum.Admin_Rejected, OfferStatusEnum.Admin_Deleted].toString(),
          sort: [{ orderBy: "createdAt", order: "DESC" }]
        }));
        // Refresh counts after offer update
        fetchAllCounts(true);
      }

      GlobalService.showToast("success", t("common.success"), t("myOffersPage.offerPermanentlyDeletedSuccess"));
    } catch (error) {
      GlobalService.showToast("error", t("common.error"), t("myOffersPage.offerPermanentlyDeleteError"));
    }
  };

  const handleCreateOrder = async (offer: any) => {
    try {
      // TODO: Implement order creation logic
      console.log("Creating order for offer:", offer);

      // For now, just show a success message
      GlobalService.showToast("success", t("common.success"), t("myOffersPage.orderCreationInitiated"));

      // You can add the actual order creation logic here
      // For example:
      // await dispatch(createOrder({ offerId: offer.id, ...otherOrderData }));

    } catch (error) {
      GlobalService.showToast("error", "Error", "Failed to create order");
    }
  };

  const togglePopover = (offerId: string) => {
    setPopoverOpen(popoverOpen === offerId ? null : offerId);
  };

  // Function to create chat and send conversation message
  const createChatAndSendMessage = async (offer: any, conversationType: ConversationTypeEnum, message: string) => {
    try {
      console.log("createChatAndSendMessage called with:", { conversationType, message, offer });
      
      const origionalOffer = offer?.origionalOffer;
      // Get client and professional IDs from offer
      const clientId = origionalOffer?.job?.userId?.id;
      const professionalId = origionalOffer?.user?.id;
      console.log("Extracted IDs:", { clientId, professionalId });

      if (!clientId || !professionalId) {
        console.warn("Missing client or professional ID in offer, skipping conversation message");
        console.log("Offer structure:", offer);
        return;
      }

      // Check if chat already exists between client and professional
      let existingChat = null;
      try {
        existingChat = await dispatch(fetchParticipantChats({ currentUserId: clientId, page: 1, limit: 50 })).unwrap();
      } catch (error) {
        // No existing chat found, will create new one
      }

      let chatResponse;
      if (existingChat.chats && existingChat.chats.length > 0) {
        const filteredChats = existingChat.chats.filter((chat: any) => {
          const participantIds = chat.chat.participants.map((p: any) => p.id);
          return participantIds.includes(clientId) && participantIds.includes(professionalId);
        });
        const chat = filteredChats[0].chat;
        chatResponse = chat;

        // Update the chat with lastMessage and lastMessageAt
        if (chatResponse && chatResponse.id) {
          const updatedChat = {
            lastMessage: message,
            lastMessageAt: new Date().toISOString(),
            unreadCount: { [clientId]: user?.id === professionalId ? 1 : 0, [professionalId]: user?.id === clientId ? 1 : 0 },
          };
          await dispatch(updateChat({
            chatId: chatResponse.id,
            data: updatedChat
          }));
        }
      } else {
        // Create new chat
        const chatPayload = {
          unreadCount: { [clientId]: user?.id === professionalId ? 0 : 1, [professionalId]: user?.id === clientId ? 0 : 1 },
          participants: [
            { id: clientId },
            { id: professionalId }
          ],
          contextId: offer?.job?.id,
          contextType: { id: ContextEnum.Message },
          lastMessageAt: new Date().toISOString(),
          lastMessage: message,
        };

        chatResponse = await dispatch(createChat({ data: chatPayload, isShowLoader: false })).unwrap();
      }

      // Send conversation message
      const conversationPayload = {
        isRead: false,
        message: message,
        sender: { id: user?.id },
        chatId: { id: chatResponse.id },
        type: { id: conversationType },
        referenceId: offer?.id,
        attachments: []
      };

      console.log("Creating conversation with payload:", conversationPayload);
      let conversation;
      try {
        conversation = await dispatch(createConversation({ data: conversationPayload, isShowLoader: false })).unwrap();
        console.log("conversation created successfully:", conversation);
      } catch (conversationError) {
        console.error("Error creating conversation:", conversationError);
        throw conversationError;
      }

      // Send WebSocket message to notify other participants (non-blocking)
      setTimeout(async () => {
        try {
          if (sendWebSocketMessage && isConnected) {
            const receivers = chatResponse.participants.map((p: any) => p.id).filter((id: any) => id !== user?.id);
            
            if (receivers.length > 0) {
              const messageData = {
                receivers,
                sender: user?.id || "",
                type: "new_conversation",
                detail: {
                  conversationId: conversation.id,
                  conversation: conversation,
                  chatId: conversation.chatId.id,
                  message: conversationPayload.message,
                  senderId: conversation.sender.id,
                  attachments: conversationPayload.attachments,
                  type: conversationPayload.type.id,
                  referenceId: conversationPayload.referenceId,
                  timestamp: new Date().toISOString()
                },
                conversationId: conversationPayload.chatId.id
              };
              
              await sendWebSocketMessage(messageData);
            }
          }
        } catch (wsError) {
          console.error("WebSocket error:", wsError);
          // Don't fail the entire operation if WebSocket fails
        }
      }, 100);
    } catch (error) {
      console.error("Error creating chat or sending message:", error);
      // Don't throw error here as the main action should still proceed
    }
  };

  // Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverOpen && !(event.target as Element).closest(".popover-container")) {
        setPopoverOpen(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [popoverOpen]);

  // Map API response to expected format
  const mapOfferFromAPI = (apiOffer: any) => {
    // Safety check for undefined or null apiOffer
    if (!apiOffer) {
      console.warn("mapOfferFromAPI: apiOffer is undefined or null");
      return null;
    }

    const timelineTypeOptions = [
      { id: OfferTimelineTypeEnum.Hour, name: "Hours" },
      { id: OfferTimelineTypeEnum.Day, name: "Days" },
      { id: OfferTimelineTypeEnum.Week, name: "Weeks" }
    ];

    const body = {
      id: apiOffer.id || apiOffer.offerId,
      type: Number(apiOffer.user.role.id) === RoleEnum.professional ? "local" : "international", // Assuming job has a type field
      jobTitle: apiOffer.title || apiOffer.job?.title || "Untitled Job",
      description: apiOffer.user.bio || "",
      origionalOffer: apiOffer,
      professional: apiOffer.user ? {
        id: apiOffer.user.id,
        name: (apiOffer.user.name || (apiOffer.user.firstName && apiOffer.user.lastName ? `${apiOffer.user.firstName} ${apiOffer.user.lastName}` : "Unknown Professional")) || "Unknown Professional",
        rating: apiOffer.user.averageRating || 0,
        reviews: apiOffer.user.reviewsCount || 0,
        completedJobs: apiOffer.user.completedJobs || 0,
        verified: apiOffer.user.verified || false,
        location: apiOffer.user.province && apiOffer.user.country ? (apiOffer.user.country.name + ", " + apiOffer.user.province) : "Unknown",
        responseRate: (typeof apiOffer.user.responseRate === "string" ? apiOffer.user.responseRate : "N/A") || "N/A",
        avgResponseTime: (typeof apiOffer.user.avgResponseTime === "string" ? apiOffer.user.avgResponseTime : "N/A") || "N/A"
      } : null,
      client: apiOffer.job?.user ? {
        name: (apiOffer.job.user.name || (apiOffer.job.user.firstName && apiOffer.job.user.lastName ? `${apiOffer.job.user.firstName} ${apiOffer.job.user.lastName}` : "Unknown User")) || "Unknown User",
        location: (typeof apiOffer.job.user.location === "string" ? apiOffer.job.user.location : "Unknown") || "Unknown",
        memberSince: apiOffer.job.user.createdAt ? new Date(apiOffer.job.user.createdAt).getFullYear().toString() : "Unknown"
      } : null,
      offer: {
        amount: apiOffer.amount || apiOffer.job?.budget || 0,
        currency: apiOffer.job?.currency?.initial || apiOffer.job?.currency?.name || apiOffer.currency?.initial || apiOffer.currency?.name || "USD",
        timeline: `${apiOffer.timeline || 1} ${timelineTypeOptions.find(type => type?.id === Number(apiOffer.timelineType?.id))?.name || "days"}`,
        timelineType: apiOffer.timelineType,
        status: (typeof apiOffer.status.id === "string" ? parseInt(apiOffer.status.id) : apiOffer.status.id) || 2,
        sentAt: apiOffer.createdAt || new Date().toISOString(),
        validUntil: apiOffer.validUntil || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
        files: apiOffer.files ? apiOffer.files : [],
        job: apiOffer.job,
        user: apiOffer.user,
        // Additional fields for local services
        availability: apiOffer.availability || [],
        includesPartsAndMaterials: apiOffer.isPartsIncluded || false,
        warranty: apiOffer.isWarranty ? "30 days" : "No warranty",
        // Additional fields for international services
        milestones: apiOffer.milestones || []
      },
      coverLetter: apiOffer.description || "No cover letter provided",
      attachments: apiOffer.files?.map((file: any) => ({
        id: file.id,
        name: file.name || file.path?.split("/").pop() || `File ${file.id}`,
        url: file.path || file.url || "",
        type: file.type || "application/octet-stream"
      }))
    };
    return body;
  };

  // Use Redux offers instead of mock data
  const receivedOffers = offers.filter(offer => offer && !offer.isSent).map(mapOfferFromAPI).filter(Boolean);
  const sentOffers = offers.filter(offer => offer && offer.isSent).map(mapOfferFromAPI).filter(Boolean);

  // Fallback to mock data if no offers are loaded
  const hasOffers = offers.length > 0;

  // Offer Card Component
  const OfferCard = ({ offer, isSentOffer = false }) => {
    const isInternational = offer.type === "international";
    const isPending = offer.offer?.status === 2;
    const handleReadMore = () => {
      setSelectedOffer(offer);
      setModalOpen(true);
    };

    // Sent offer card
    if (isSentOffer) {
      return (
        <Card
          className="p-4 md:p-6 hover:shadow-lg transition-all duration-200 cursor-pointer hover:scale-[1.02]"
          onClick={() => {
            console.log("Card clicked, offer:", offer);
            setSelectedOffer(offer);
            setModalOpen(true);
          }}
        >
          <div className="flex flex-col gap-4">
            <div className="flex items-start justify-between">
              <div className="space-y-1">
                {/* Professional Info */}
                {offer.professional ? (
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center shrink-0 overflow-hidden">

                      {(() => {

                        const p = offer.professional as any;
                        const photoUrl = offer?.origionalOffer?.user?.photo?.path || "";
                        return photoUrl ? (
                          <img src={photoUrl} alt={p?.name || "Professional"} className="h-12 w-12 object-cover" />
                        ) : (
                          <User className="h-6 w-6 text-gray-600" />
                        );
                      })()}
                    </div>
                    <div className="space-y-1 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium">{offer.professional.name}</span>
                        {offer.professional.verified && (
                          <Badge variant={undefined} className="bg-green-50 text-green-700">{t("myOffersPage.verified")}</Badge>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          <Star className="h-4 w-4 text-yellow-400 fill-current" />
                          <span>{offer.professional.rating}</span>
                        </div>
                        <span>({offer.professional.reviews} {t("myOffersPage.reviews")})</span>
                        <span className="hidden md:inline">•</span>
                        <span>{offer.professional.completedJobs} {t("myOffersPage.jobsCompleted")}</span>
                      </div>
                      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          <Timer className="h-4 w-4" />
                          <span>{offer.professional.avgResponseTime} {t("myOffersPage.response")}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <MapPin className="h-4 w-4" />
                          <span>
                            {offer?.origionalOffer?.serviceAreas && offer.origionalOffer.serviceAreas.length > 0
                              ? offer.origionalOffer.serviceAreas
                                .slice(0, 2)
                                .map((a: any) => (a?.province ? `${a.province}, ${a?.country?.name || ""}` : (a?.country?.name || "")))
                                .filter(Boolean)
                                .join(" | ")
                              : offer.professional.location}
                          </span>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedProfessional(offer.professional);
                        setShowProfileDialog(true);
                      }}
                      className="text-xs text-blue-600 hover:text-blue-700 hover:underline self-start md:self-center"
                    >
                      {t("myOffersPage.viewProfile")}
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
                      <User className="h-6 w-6 text-gray-600" />
                    </div>
                    <div className="space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium text-gray-500">{t("myOffersPage.professionalNotAvailable")}</span>
                      </div>
                    </div>
                  </div>
                )}
                <br />
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="text-base md:text-lg font-semibold text-gray-900">
                    {offer.jobTitle}
                  </h3>
                  <Badge variant={undefined} className={`
                    ${offer.type === "international" ? "bg-blue-100 text-blue-800" : "bg-green-100 text-green-800"}
                  `}>
                    {offer.type === "international" ? t("myOffersPage.international") : t("myOffersPage.local")}
                  </Badge>
                  {offer.origionalOffer?.job?.isEmergency && (
                    <Badge variant={undefined} className="bg-red-100 text-red-800 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      {t("myOffersPage.emergencyExitFee")} {getDynamicCurrencySymbol()}{calculateEmergencyCost(
                        offer.origionalOffer.job?.emergencyTimeSlot,
                        offer.origionalOffer.job?.emergencyDays,
                        categoryFormSettings
                      )}
                    </Badge>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
                </div>
              </div>
              <div className="relative">
                <Button
                  variant="ghost"
                  size="sm"
                  className="shrink-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    togglePopover(offer.id);
                  }}
                >
                  <MoreHorizontal className="h-5 w-5" />
                </Button>

                {/* Popover Menu */}
                {popoverOpen === offer.id && (
                  <div className="popover-container absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                    <div className="py-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditOffer(offer);
                        }}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                      >
                        <FileText className="h-4 w-4" />
                        {t("myOffersPage.editOffer")}
                      </button>
                      {offer.offer.status != OfferStatusEnum.Admin_Suspended && offer.offer.status != OfferStatusEnum.Admin_Deleted && (
                        <>
                          {offer.offer.status === OfferStatusEnum.Cancelled ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleUpdateStatusOffer(offer, OfferStatusEnum.Approved);
                              }}
                              className="w-full px-4 py-2 text-left text-sm text-green-600 hover:bg-green-50 flex items-center gap-2"
                            >
                              <CheckCircle className="h-4 w-4" />
                              {t("myOffersPage.approveOffer")}
                            </button>
                          ) : (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleUpdateStatusOffer(offer, OfferStatusEnum.Cancelled);
                              }}
                              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                            >
                              <AlertCircle className="h-4 w-4" />
                              {t("myOffersPage.suspendOffer")}
                            </button>
                          )}

                          {offer.offer.status !== OfferStatusEnum.Archived ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteOffer(offer);
                              }}
                              className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                            >
                              <X className="h-4 w-4" />
                              {t("myOffersPage.deleteOffer")}
                            </button>
                          ) : (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleUpdateStatusOffer(offer, OfferStatusEnum.Approved);
                              }}
                              className="w-full px-4 py-2 text-left text-sm text-green-600 hover:bg-green-50 flex items-center gap-2"
                            >
                              <CheckCircle className="h-4 w-4" />
                              {t("myOffersPage.activateOffer")}
                            </button>
                          )}
                        </>
                      )}

                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-gray-600">{t("myOffersPage.offeredAmount")}</div>
                <div className="text-lg font-semibold">
                  {getDynamicCurrencySymbol()}{offer.offer.amount.toLocaleString()}
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-600">{t("myOffersPage.timeline")}</div>
                <div className="font-medium">{offer.offer.timeline}</div>
              </div>
            </div>

            <div className="flex flex-col md:flex-row md:items-center justify-between gap-2">
              <Badge variant={undefined} className={`
                w-fit ${offer.offer.status === 3 ? "bg-green-100 text-green-800" :
                  offer.offer.status === 4 ? "bg-red-100 text-red-800" :
                    "bg-gray-100 text-gray-800"}
              `}>
                {offer.offer.status === 2 ? "Approved"
                  : offer.offer.status === 3 ? "Accepted"
                    : offer.offer.status === 4 ? "Rejected"
                      : offer.offer.status === 5 ? "Expired"
                        : offer.offer.status === 6 ? "Cancelled"
                          : offer.offer.status === 7 ? "Completed"
                            : offer.offer.status === 8 ? "Archived"
                              : offer.offer.status === 9 ? "Deleted"
                                : offer.offer.status === 10 ? "Permanently Deleted"
                                  : offer.offer.status === 11 ? "Suspended"
                                    : offer.offer.status === 12 ? "Rejected"
                                      : offer.offer.status === 13 ? "Admin Deleted"
                                        : offer.offer.status === 14 ? "Admin Permanently Deleted"
                                          : "Unknown"
                }
              </Badge>

              <>
                {offer.offer.status != OfferStatusEnum.Admin_Suspended && offer.offer.status != OfferStatusEnum.Admin_Deleted && (
                  <div className="flex items-center gap-2">
                    <Button
                      className="flex items-center gap-2 bg-green-500 hover:bg-green-600"
                      onClick={() => {
                        window.location.href = `/client/MessageAndVideoCall?professionalId=${offer.offer.user.id}&professionalName=${encodeURIComponent(offer.offer.user.firstName)}`;
                        // navigate(`/client/MessageAndVideoCall?professionalId=${offer.offer.user.id}&professionalName=${encodeURIComponent(offer.offer.user.firstName)}`);
                      }}
                    >
                      <CheckCircle className="h-4 w-4" />
                      {t("myOffersPage.chat")}
                    </Button>
                    {offer.offer.status === 3 && (<Button
                      className="flex items-center gap-2 bg-blue-400 hover:bg-blue-500"
                      onClick={(e) => {
                        e.stopPropagation();
                        setContractOffer(offer.origionalOffer);
                        setShowContractModal(true);
                      }}
                    >
                      <CheckCircle className="h-4 w-4" />
                      {t("myOffersPage.createContract")}
                    </Button>
                    )}
                  </div>
                )}
              </>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedOffer(offer);
                      setModalOpen(true);
                    }}
                    className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-1"
                  >
                    <Eye className="h-4 w-4" />
                    {t("myOffersPage.seeMore")}
                  </button>
                  <div className="text-sm text-gray-500">
                    {t("myOffersPage.sent")} {new Date(offer.offer.sentAt).toLocaleDateString()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>
      );
    }

    // Received offer card
    return (
      <Card
        className="p-4 md:p-6 hover:shadow-lg transition-all duration-200 cursor-pointer hover:scale-[1.02]"
        style={{ textAlign: "left" }}
        onClick={() => {
          console.log("Received offer card clicked, offer:", offer);
          setSelectedOffer(offer);
          setModalOpen(true);
        }}
      >
        <div className="flex flex-col gap-6">
          {/* Professional Info */}
          {offer.professional ? (
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              <div className="h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center shrink-0 overflow-hidden">

                {(() => {

                  const p = offer.professional as any;
                  const photoUrl = offer?.origionalOffer?.user?.photo?.path || "";
                  return photoUrl ? (
                    <img src={photoUrl} alt={p?.name || "Professional"} className="h-12 w-12 object-cover" />
                  ) : (
                    <User className="h-6 w-6 text-gray-600" />
                  );
                })()}
              </div>
              <div className="space-y-1 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{offer.professional.name}</span>
                  {offer.professional.verified && (
                    <Badge variant={undefined} className="bg-green-50 text-green-700">Verified</Badge>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
                  <div className="flex items-center gap-1">
                    <Star className="h-4 w-4 text-yellow-400 fill-current" />
                    <span>{offer.professional.rating}</span>
                  </div>
                  <span>({offer.professional.reviews} reviews)</span>
                  <span className="hidden md:inline">•</span>
                  <span>{offer.professional.completedJobs} jobs completed</span>
                </div>
                <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                  <div className="flex items-center gap-1">
                    <Timer className="h-4 w-4" />
                    <span>{offer.professional.avgResponseTime} response</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <MapPin className="h-4 w-4" />
                    <span>
                      {offer?.origionalOffer?.serviceAreas && offer.origionalOffer.serviceAreas.length > 0
                        ? offer.origionalOffer.serviceAreas
                          .slice(0, 2)
                          .map((a: any) => (a?.province ? `${a.province}, ${a?.country?.name || ""}` : (a?.country?.name || "")))
                          .filter(Boolean)
                          .join(" | ")
                        : offer.professional.location}
                    </span>
                  </div>
                </div>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedProfessional(offer.professional);
                  setShowProfileDialog(true);
                }}
                className="text-xs text-blue-600 hover:text-blue-700 hover:underline self-start md:self-center"
              >
                View Profile
              </button>
            </div>
          ) : (
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              <div className="h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center shrink-0 overflow-hidden">
                {(() => {
                  const photoUrl = offer?.origionalOffer?.user?.photo?.path || "";
                  return photoUrl ? (
                    <img src={photoUrl} alt="User" className="h-12 w-12 object-cover" />
                  ) : (
                    <User className="h-6 w-6 text-gray-600" />
                  );
                })()}
              </div>
              <div className="space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-gray-500">Professional information not available</span>
                </div>
              </div>
            </div>
          )}
          {/* Header */}
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-2">
              <p className="text-sm text-gray-600">
                {offer.description}
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="text-base md:text-lg font-semibold text-gray-900">
                  {offer.jobTitle}
                </h3>
                <Badge variant={undefined} className={`
                  ${offer.type === "international" ? "bg-blue-100 text-blue-800" : "bg-green-100 text-green-800"}
                `}>
                  {offer.type === "international" ? "International" : "Local"}
                </Badge>
                {offer.origionalOffer?.job?.isEmergency && (
                  <Badge variant={undefined} className="bg-red-100 text-red-800 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    Emergency exit fee: {getDynamicCurrencySymbol()}{calculateEmergencyCost(
                      offer.origionalOffer.job?.emergencyTimeSlot,
                      offer.origionalOffer.job?.emergencyDays,
                      categoryFormSettings
                    )}
                  </Badge>
                )}
                <span className="text-sm font-medium text-gray-700">Status:</span>
                <Badge variant={undefined} className={`
                    ${offer.offer.status === 3 ? "bg-green-100 text-green-800" :
                    offer.offer.status === 4 ? "bg-red-100 text-red-800" :
                      offer.offer.status === 10 ? "bg-red-900 text-white" :
                        "bg-gray-100 text-gray-800"}
                  `}>
                  {offer.offer.status === 2 ? "Approved"
                    : offer.offer.status === 3 ? "Accepted"
                      : offer.offer.status === 4 ? "Rejected"
                        : offer.offer.status === 5 ? "Expired"
                          : offer.offer.status === 6 ? "Cancelled"
                            : offer.offer.status === 7 ? "Completed"
                              : offer.offer.status === 8 ? "Archived"
                                : offer.offer.status === 9 ? "Deleted"
                                  : offer.offer.status === 10 ? "Permanently Deleted"
                                    : offer.offer.status === 11 ? "Suspended"
                                      : offer.offer.status === 12 ? "Rejected"
                                        : offer.offer.status === 13 ? "Admin Deleted"
                                          : offer.offer.status === 14 ? "Permanently Deleted"
                                            : "Unknown"
                  }
                </Badge>
              </div>
            </div>
          </div>



          {/* Offer Details */}
          <div className="space-y-6">
            {/* Price and Timeline */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-gray-600">{t("myOffersPage.budget")}</div>
                <div className="text-lg font-semibold">
                  {getDynamicCurrencySymbol()}{offer.offer.amount.toLocaleString()}
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-600">{t("myOffersPage.timeline")}</div>
                <div className="font-medium">{offer.offer.timeline}</div>
              </div>
              <div className="md:col-span-2">
                <div className="text-sm text-gray-600">{t("myOffersPage.serviceAreas")}</div>
                <div className="font-medium text-sm text-gray-700">
                  {offer?.origionalOffer?.serviceAreas && offer.origionalOffer.serviceAreas.length > 0
                    ? offer.origionalOffer.serviceAreas
                      .slice(0, 2)
                      .map((a: any) => (a?.province ? `${a.province}, ${a?.country?.name || ""}` : (a?.country?.name || "")))
                      .filter(Boolean)
                      .join(" | ")
                    : (offer.professional?.location || t("myOffersPage.unknown"))}
                </div>
              </div>
            </div>

            {/* Milestones for International Projects */}
            {isInternational && offer.offer.milestones && (
              <div className="space-y-2">
                <h4 className="font-medium">{t("myOffersPage.proposedMilestones")}</h4>
                <div className="space-y-2 overflow-x-auto">
                  <div className="min-w-[600px]">
                    {offer.offer.milestones.map((milestone, index) => (
                      <div key={index} className="flex justify-between items-center text-sm border-b pb-2">
                        <div>
                          <span className="font-medium">{milestone.title}</span>
                          <span className="text-gray-600 ml-2">({milestone.duration})</span>
                        </div>
                        <span className="font-medium">
                          {getDynamicCurrencySymbol()}{milestone.amount.toLocaleString()}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Availability for Local Services */}
            {!isInternational && offer.offer.availability && (
              <div className="space-y-2">
                <h4 className="font-medium">{t("myOffersPage.availableTimeSlots")}</h4>
                <div className="space-y-2">
                  {offer.offer.availability.map((slot, index) => (
                    <div key={index} className="flex flex-wrap items-center gap-2 text-sm">
                      <Calendar className="h-4 w-4 text-gray-400 shrink-0" />
                      <span>{new Date(slot.date).toLocaleDateString()}: </span>
                      <span className="text-gray-600">
                        {slot.slots.join(", ")}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Emergency Exit Cost */}
            {offer.origionalOffer?.job?.isEmergency && (
              <div className="space-y-2">
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-red-600" />
                    <span className="text-sm font-medium text-red-800">{t("myOffersPage.emergencyService")}</span>
                  </div>
                  <p className="text-xs text-red-700 mt-1">
                    Emergency exit fee: {getDynamicCurrencySymbol()}{calculateEmergencyCost(
                      offer.origionalOffer.job?.emergencyTimeSlot,
                      offer.origionalOffer.job?.emergencyDays,
                      categoryFormSettings
                    )}
                  </p>
                </div>
              </div>
            )}

            {/* Additional Details */}
            {!isInternational && (
              <div className="flex flex-wrap gap-4 text-sm">
                {offer.offer.includesPartsAndMaterials && (
                  <div className="flex items-center gap-1 text-green-600">
                    <CheckCircle className="h-4 w-4 shrink-0" />
                    <span>{t("myOffersPage.partsAndMaterialsIncluded")}</span>
                  </div>
                )}
                {offer.offer.warranty && (
                  <div className="flex items-center gap-1 text-blue-600">
                    <Shield className="h-4 w-4 shrink-0" />
                    <span>{offer.offer.warranty} {t("myOffersPage.warranty")}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Cover Letter Preview */}
          <div>
            <p className="text-gray-600 text-sm line-clamp-3">
              {offer.coverLetter}
            </p>
            <button
              className="text-blue-600 text-sm hover:text-blue-700 mt-1"
              onClick={handleReadMore}
            >
              {t("myOffersPage.readMore")}
            </button>
          </div>

          {/* Attachments */}
          {offer.attachments?.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {offer.attachments.map((file, index) => (
                <div key={index} className="flex items-center gap-2 text-sm bg-gray-50 px-3 py-1.5 rounded-lg">
                  <FileText className="h-4 w-4 text-gray-400 shrink-0" />
                  <span>{file.name}</span>
                </div>
              ))}
            </div>
          )}



          {/* Additional Action Buttons for Received Offers */}
          {selectedTab === "received" && offer.offer.status !== 9 && offer.offer.status !== 10 && offer.offer.status !== 8 && offer.offer.status !== OfferStatusEnum.Admin_Suspended && offer.offer.status !== OfferStatusEnum.Admin_Deleted && (
            <div className="flex flex-col sm:flex-row sm:justify-between gap-2 mt-4">
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedOffer(offer);
                    setModalOpen(true);
                  }}
                  className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-1 px-3 py-2 hover:bg-blue-50 rounded-lg flex-1 sm:flex-none"
                >
                  <Eye className="h-4 w-4" />
                  {t("myOffersPage.seeMore")}
                </button>
                {offer.offer.status !== 4 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeclineOffer(offer);
                    }}
                    className="px-3 py-2 border rounded-lg hover:bg-gray-50 text-red-600 text-sm sm:text-base flex-1 sm:flex-none"
                  >
                    {t("myOffersPage.declineOffer")}
                  </button>
                )}
                {offer.offer.status !== 3 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAcceptOffer(offer);
                    }}
                    className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm sm:text-base flex-1 sm:flex-none"
                  >
                    {t("myOffersPage.acceptOffer")}
                  </button>
                )}
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteOffer(offer);
                  }}
                  className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm sm:text-base flex-1 sm:flex-none"
                >
                  {t("myOffersPage.delete")}
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    window.location.href = "/client/MessageAndVideoCall?professionalId=" + offer.professional.id + "&professionalName=" + encodeURIComponent(offer.professional.name);
                    //navigate(`/client/MessageAndVideoCall?professionalId=${offer.professional.id}&professionalName=${encodeURIComponent(offer.professional.name)}`);
                  }}
                  className="px-3 py-2 border rounded-lg hover:bg-gray-50 text-green-600 text-sm sm:text-base flex-1 sm:flex-none"
                >
                  {t("myOffersPage.message")}
                </button>
                {offer.offer.status === OfferStatusEnum.Accepted && <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setContractOffer(offer.origionalOffer);
                    setShowContractModal(true);
                  }}
                  className="px-3 py-2 border rounded-lg hover:bg-gray-50 bg-green-500 text-white text-sm sm:text-base flex-1 sm:flex-none"
                >
                  {t("myOffersPage.createContract")}
                </button>}
              </div>
            </div>
          )}

          {/* Permanent Delete Button for Deleted/Archived Offers */}
          {selectedTab === "received" && (offer.offer.status === 9 || offer.offer.status === 8 || offer.offer.status === OfferStatusEnum.Admin_Deleted) && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mt-4">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedOffer(offer);
                    setModalOpen(true);
                  }}
                  className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-1 px-3 py-2 hover:bg-blue-50 rounded-lg"
                >
                  <Eye className="h-4 w-4" />
                  {t("myOffersPage.seeMore")}
                </button>
                <p className="text-sm text-gray-600">
                  {offer.offer.status != OfferStatusEnum.Admin_Deleted ? t("myOffersPage.deletedByProfessional") : t("myOffersPage.deletedByAdmin")}
                </p>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handlePermanentlyDeleteOffer(offer);
                }}
                className="px-3 py-2 bg-red-900 text-white rounded-lg hover:bg-red-950 text-sm sm:text-base"
              >
                {t("myOffersPage.permanentlyDelete")}
              </button>
            </div>
          )}
        </div>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50" style={{ textAlign: "left" }}>
      <SharedHeader5 />

      <main className="max-w-7xl mx-auto px-4 py-6 md:py-8">
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 md:mb-8">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-gray-900">{t("myOffersPage.title")}</h1>
            <p className="text-gray-600">
              {t("myOffersPage.subtitle")}
            </p>
          </div>
        </div>

        {/* Filters and Views */}
        <div className="bg-white border rounded-lg p-4 mb-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            {/* Left side - Tabs */}
            <div className="flex items-center gap-2 md:gap-4 w-full sm:w-auto">
              <button
                onClick={() => handleTabChange("received")}
                className={`flex-1 sm:flex-none px-3 md:px-4 py-2 rounded-lg text-sm font-medium ${selectedTab === "received"
                  ? "bg-blue-50 text-blue-700"
                  : "text-gray-600 hover:bg-gray-50"
                  }`}
              >
                {t("myOffersPage.received")} ({countsLoading && !countsInitialized ? "..." : receivedTabCount})
              </button>
              <button
                onClick={() => handleTabChange("sent")}
                className={`flex-1 sm:flex-none px-3 md:px-4 py-2 rounded-lg text-sm font-medium ${selectedTab === "sent"
                  ? "bg-blue-50 text-blue-700"
                  : "text-gray-600 hover:bg-gray-50"
                  }`
                }
              >
                {t("myOffersPage.sent")} ({countsLoading && !countsInitialized ? "..." : sentTabCount})
              </button>
            </div>

            {/* Right side - Actions */}
            <div className="flex flex-wrap items-center gap-2 md:gap-3 w-full sm:w-auto">
              {/* Search */}
              <div className="relative flex-1 sm:flex-none">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder={t("myOffersPage.searchOffers")}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full sm:w-auto pl-9 pr-4 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Filter Button */}
              <Button
                variant="outline"
                className="flex items-center gap-2"
                onClick={() => setFilterOpen(!filterOpen)}
              >
                <Filter className="h-4 w-4" />
                <span className="hidden md:inline">{t("myOffersPage.filters")}</span>
                <ChevronDown className="h-4 w-4" />
              </Button>

              {/* View Toggle */}
              <div className="flex border rounded-lg overflow-hidden">
                <button
                  onClick={() => setViewMode("list")}
                  className={`p-2 ${viewMode === "list"
                    ? "bg-blue-50 text-blue-700"
                    : "bg-white text-gray-600 hover:bg-gray-50"
                    }`}
                >
                  <List className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setViewMode("grid")}
                  className={`p-2 ${viewMode === "grid"
                    ? "bg-blue-50 text-blue-700"
                    : "bg-white text-gray-600 hover:bg-gray-50"
                    }`}
                >
                  <Grid className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Filter Panel */}
          {filterOpen && (
            <div className="mt-4 pt-4 border-t">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Offer Type Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("myOffersPage.offerType")}
                  </label>
                  <select
                    value={filters.offerType}
                    onChange={(e) => handleFilterChange("offerType", e.target.value)}
                    className="w-full p-2 border rounded-lg"
                  >
                    <option value="all">{t("myOffersPage.allTypes")}</option>
                    <option value="local">{t("myOffersPage.localServices")}</option>
                    <option value="international">{t("myOffersPage.internationalServices")}</option>
                  </select>
                </div>

                {/* Status Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("myOffersPage.status")}
                  </label>
                  <select
                    value={filters.status}
                    onChange={(e) => handleFilterChange("status", e.target.value)}
                    className="w-full p-2 border rounded-lg"
                  >
                    <option value="all">{t("myOffersPage.allStatuses")}</option>
                    <option value="approved">{t("myOffersPage.approved")}</option>
                    <option value="accepted">{t("myOffersPage.accepted")}</option>
                    <option value="rejected">{t("myOffersPage.rejected")}</option>
                    <option value="expired">{t("myOffersPage.expired")}</option>
                    <option value="cancelled">{t("myOffersPage.cancelled")}</option>
                    <option value="completed">{t("myOffersPage.completed")}</option>
                    <option value="archived">{t("myOffersPage.archived")}</option>
                    <option value="permanently_deleted">{t("myOffersPage.permanentlyDeleted")}</option>
                  </select>
                </div>

                {/* Timeframe Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("myOffersPage.timeframe")}
                  </label>
                  <select
                    value={filters.timeframe}
                    onChange={(e) => handleFilterChange("timeframe", e.target.value)}
                    className="w-full p-2 border rounded-lg"
                  >
                    <option value="all">{t("myOffersPage.allTime")}</option>
                    <option value="today">{t("myOffersPage.today")}</option>
                    <option value="this-week">{t("myOffersPage.thisWeek")}</option>
                    <option value="this-month">{t("myOffersPage.thisMonth")}</option>
                    <option value="last-3-months">{t("myOffersPage.last3Months")}</option>
                  </select>
                </div>
              </div>

              {/* Filter Actions */}
              <div className="flex flex-col sm:flex-row sm:justify-end gap-2 mt-4">
                <Button
                  variant="outline"
                  className="w-full sm:w-auto"
                  onClick={() => {
                    setFilters({
                      offerType: "all",
                      status: "all",
                      timeframe: "all"
                    });
                    setSearchTerm("");
                    setLocalCurrentPage(1);
                  }}
                >
                  {t("myOffersPage.clearFilters")}
                </Button>
                <Button className="w-full sm:w-auto">
                  {t("myOffersPage.applyFilters")}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Loading State */}
        {(loading || isFiltering) && (
          <div className="text-center py-8 md:py-12">
            <div className="mb-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {isFiltering ? t("myOffersPage.applyingFilters") : t("myOffersPage.loadingOffers")}
            </h3>
            <p className="text-gray-600">
              {isFiltering ? t("myOffersPage.pleaseWaitApplyingFilters") : t("myOffersPage.pleaseWaitFetchingOffers")}
            </p>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="text-center py-8 md:py-12">
            <div className="mb-4">
              <AlertCircle className="h-12 w-12 text-red-400 mx-auto" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {t("myOffersPage.errorLoadingOffers")}
            </h3>
            <p className="text-gray-600 mb-4">
              {error}
            </p>
            <Button
              onClick={() => {
                // Retry fetching offers
                if (user?.id) {
                  setLocalCurrentPage(1);
                  dispatch(fetchUserOffers({
                    page: 1,
                    limit: pageSize,
                    jobUserId: user.id,
                    isSent: selectedTab === "sent",
                    search: searchTerm || undefined,
                    statusIds: [OfferStatusEnum.Approved, OfferStatusEnum.Accepted, OfferStatusEnum.Rejected, OfferStatusEnum.Cancelled, OfferStatusEnum.Archived, OfferStatusEnum.Admin_Suspended, OfferStatusEnum.Admin_Rejected, OfferStatusEnum.Admin_Deleted].toString(),
                    sort: [{ orderBy: "createdAt", order: "DESC" }]
                  }));
                }
              }}
            >
              {t("myOffersPage.tryAgain")}
            </Button>
          </div>
        )}

        {/* Offers Grid/List */}
        {!loading && !error && (
          <div className={
            viewMode === "grid"
              ? "grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6"
              : "space-y-4 md:space-y-6"
          }>
            {selectedTab === "received" ? (
              receivedOffers.length > 0 ? (
                receivedOffers.map(offer => (
                  <OfferCard key={offer.id} offer={offer} />
                ))
              ) : (
                <div className="text-center py-8 md:py-12">
                  <div className="mb-4">
                    <Package className="h-12 w-12 text-gray-400 mx-auto" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                      {searchTerm || filters.status !== "all" || filters.offerType !== "all" || filters.timeframe !== "all"
                      ? t("myOffersPage.noOffersFound")
                      : t("myOffersPage.noOffersReceived")
                    }
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {searchTerm || filters.status !== "all" || filters.offerType !== "all" || filters.timeframe !== "all"
                      ? t("myOffersPage.adjustSearchTerms")
                      : t("myOffersPage.noOffersReceivedMessage")
                    }
                  </p>
                </div>
              )
            ) : (
              sentOffers.length > 0 ? (
                sentOffers.map(offer => (
                  <OfferCard key={offer.id} offer={offer} isSentOffer={true} />
                ))
              ) : (
                <div className="text-center py-8 md:py-12">
                  <div className="mb-4">
                    <Send className="h-12 w-12 text-gray-400 mx-auto" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    {searchTerm || filters.status !== "all" || filters.offerType !== "all" || filters.timeframe !== "all"
                      ? t("myOffersPage.noOffersFound")
                      : t("myOffersPage.noOffersSent")
                    }
                  </h3>
                  <p className="text-gray-600">
                    {searchTerm || filters.status !== "all" || filters.offerType !== "all" || filters.timeframe !== "all"
                      ? t("myOffersPage.adjustSearchTerms")
                      : t("myOffersPage.noOffersSentMessage")
                    }
                  </p>
                </div>
              )
            )}
          </div>
        )}

        {/* Pagination */}
        {!loading && !error && (selectedTab === "received" ? receivedOffers : sentOffers).length > 0 && (totalCount > pageSize || hasNextPage) && (
          <div className="mt-6 md:mt-8">
            <nav className="flex flex-col sm:flex-row justify-center items-center gap-2">
              <Button
                variant="outline"
                className="w-full sm:w-24"
                disabled={localCurrentPage === 1}
                onClick={() => {
                  if (user?.id && localCurrentPage > 1) {
                    console.log("Previous clicked, changing page from", localCurrentPage, "to", localCurrentPage - 1);
                    setLocalCurrentPage(localCurrentPage - 1);
                  }
                }}
              >
                {t("myOffersPage.previous")}
              </Button>


              <Button
                variant="outline"
                className="w-full sm:w-24"
                disabled={!hasNextPage && localCurrentPage >= (totalPages || Math.ceil(totalCount / pageSize))}
                onClick={() => {
                  if (user?.id && (hasNextPage || localCurrentPage < (totalPages || Math.ceil(totalCount / pageSize)))) {
                    console.log("Next clicked, changing page from", localCurrentPage, "to", localCurrentPage + 1);
                    setLocalCurrentPage(localCurrentPage + 1);
                  }
                }}
              >
                {t("myOffersPage.next")}
              </Button>
            </nav>

            {/* Pagination Info */}
            <div className="mt-4 text-center text-sm text-gray-600">
              {t("myOffersPage.showingPage", { current: localCurrentPage, total: Math.ceil(totalCount / pageSize), count: totalCount })}
            </div>
          </div>
        )}

        {/* Offer Details Modal */}
        {selectedOffer && (
          <ClientOfferDetailsModal
            isOpen={modalOpen}
            onClose={() => {
              setModalOpen(false);
              setSelectedOffer(null);
            }}
            offer={selectedOffer}
            categoryFormSettings={categoryFormSettings}
            showActions={true}
            onOfferUpdated={() => {
              // Refresh offers after update
              if (user?.id) {
                dispatch(fetchUserOffers({
                  page: localCurrentPage,
                  limit: pageSize,
                  jobUserId: user.id,
                  isSent: selectedTab === "sent",
                  search: searchTerm || undefined,
                  status: filters.status !== "all" ? (filters.status === "approved" ? 2 : filters.status === "accepted" ? 3 : filters.status === "rejected" ? 4 : filters.status === "expired" ? 5 : filters.status === "cancelled" ? 6 : filters.status === "completed" ? 7 : filters.status === "archived" ? 8 : filters.status === "deleted" ? 9 : undefined) : undefined,
                  statusIds: [OfferStatusEnum.Approved, OfferStatusEnum.Accepted, OfferStatusEnum.Rejected, OfferStatusEnum.Cancelled, OfferStatusEnum.Archived, OfferStatusEnum.Admin_Suspended, OfferStatusEnum.Admin_Rejected, OfferStatusEnum.Admin_Deleted].toString(),
                  sort: [{ orderBy: "createdAt", order: "DESC" }]
                }));
              }
            }}
            currentPage={localCurrentPage}
            pageSize={pageSize}
            selectedTab={selectedTab}
            searchTerm={searchTerm}
            filters={filters}
            userId={user?.id}
          />
        )}

        {/* Request Quote Modal for Editing */}
        {showRequestQuoteModal && editingOffer && (
          <RequestQuoteModal
            isOpen={showRequestQuoteModal}
            onClose={() => {
              setShowRequestQuoteModal(false);
              setEditingOffer(null);
            }}
            professional={editingOffer.professional || {}}
            offerId={editingOffer.id}
            existingOffer={editingOffer}
            onOfferUpdated={(updatedOffer) => {
              // Refresh offers after update
              if (user?.id) {
                dispatch(fetchUserOffers({
                  page: localCurrentPage,
                  limit: pageSize,
                  jobUserId: user.id,
                  isSent: selectedTab === "sent",
                  search: searchTerm || undefined,
                  status: filters.status !== "all" ? (filters.status === "approved" ? 2 : filters.status === "accepted" ? 3 : filters.status === "rejected" ? 4 : filters.status === "expired" ? 5 : filters.status === "cancelled" ? 6 : filters.status === "completed" ? 7 : filters.status === "archived" ? 8 : filters.status === "deleted" ? 9 : undefined) : undefined,
                  statusIds: [OfferStatusEnum.Approved, OfferStatusEnum.Accepted, OfferStatusEnum.Rejected, OfferStatusEnum.Cancelled, OfferStatusEnum.Archived, OfferStatusEnum.Admin_Suspended, OfferStatusEnum.Admin_Rejected, OfferStatusEnum.Admin_Deleted].toString(),
                  sort: [{ orderBy: "createdAt", order: "DESC" }]
                }));
              }
              setShowRequestQuoteModal(false);
              setEditingOffer(null);
              GlobalService.showToast("success", t("common.success"), t("clientOfferDetailsModal.offerAcceptedSuccess"));
            }}
          />
        )}
      </main>

      {/* Professional Profile Dialog */}
      <AnimatePresence>
        {showProfileDialog && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 z-50"
              onClick={() => setShowProfileDialog(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="fixed inset-4 z-50 overflow-hidden"
            >
              <div className="h-full bg-white rounded-lg shadow-xl flex flex-col">
                {/* Dialog Header */}
                <div className="flex items-center justify-between p-4 border-b">
                  <h2 className="text-lg font-semibold text-gray-900">
                    {t("myOffersPage.profileTitle", { name: selectedProfessional?.name })}
                  </h2>
                  <button
                    onClick={() => setShowProfileDialog(false)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-500" />
                  </button>
                </div>

                {/* Dialog Content */}
                <div className="flex-1 overflow-y-auto p-4">
                  {selectedProfessional && (
                    <MyProfile
                      isAdminEdit={true}
                      isViewing={true}
                      adminUserId={selectedProfessional.id}
                      onUserUpdated={() => {
                        // Refresh the professionals list if needed
                        // handleSearch();
                      }}
                    />
                  )}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Contract / Milestone Modal */}
      <ContractMilestoneModal
        isOpen={showContractModal}
        offer={contractOffer}
        onClose={() => setShowContractModal(false)}
      />

      <br></br>
      <br></br>

      <br></br>
      <br></br>
      <br></br>

      {!loading && <SharedFooter3 />}
    </div>
  );
};

export default MyOffersPage;